/**
 * Ride Payment Component
 * Isolated ride component - does not touch package system
 * Supports both JMD (Fygaro) and USD (Stripe) payments
 */
import { CreditCard, Wallet, DollarSign, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PAYMENT_METHODS = [
  {
    id: 'fygaro',
    name: 'Pay in JMD',
    description: 'Fygaro - Local payment',
    icon: Wallet,
    currency: 'JMD'
  },
  {
    id: 'stripe',
    name: 'Pay in USD',
    description: 'Stripe - International card',
    icon: CreditCard,
    currency: 'USD'
  }
];

const RidePayment = ({ 
  selected, 
  onSelect, 
  fareJMD,
  fareUSD,
  onConfirm,
  loading = false,
  disabled = false
}) => {
  const formatCurrency = (amount, currency) => {
    if (currency === 'USD') {
      return `$${(Number(amount) || 0).toFixed(2) || '0.00'} USD`;
    }
    return `J$${amount?.toLocaleString() || '0'}`;
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-emerald-600" />
        Payment Method
      </h3>

      <div className="grid grid-cols-2 gap-3">
        {PAYMENT_METHODS.map((method) => {
          const isSelected = selected === method.id;
          const Icon = method.icon;
          const fare = method.currency === 'USD' ? fareUSD : fareJMD;
          
          return (
            <button
              key={method.id}
              type="button"
              onClick={() => !disabled && onSelect(method.id)}
              disabled={disabled}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                isSelected 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              data-testid={`payment-${method.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-6 h-6 ${isSelected ? 'text-emerald-600' : 'text-gray-500'}`} />
                {isSelected && <Check className="w-5 h-5 text-emerald-600" />}
              </div>
              <h4 className="font-semibold text-gray-800">{method.name}</h4>
              <p className="text-xs text-gray-500 mb-2">{method.description}</p>
              <p className={`text-lg font-bold ${isSelected ? 'text-emerald-600' : 'text-gray-700'}`}>
                {formatCurrency(fare, method.currency)}
              </p>
            </button>
          );
        })}
      </div>

      {/* Confirm Button */}
      <Button
        onClick={onConfirm}
        disabled={!selected || disabled || loading}
        className="w-full py-6 text-lg bg-emerald-600 hover:bg-emerald-700"
        data-testid="confirm-ride-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
            Processing...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Confirm & Pay {selected && formatCurrency(
              selected === 'stripe' ? fareUSD : fareJMD,
              selected === 'stripe' ? 'USD' : 'JMD'
            )}
          </span>
        )}
      </Button>

      {/* Payment note */}
      <p className="text-xs text-gray-500 text-center">
        {selected === 'stripe' 
          ? 'You will be redirected to Stripe for secure USD payment'
          : 'Payment will be processed via Fygaro in JMD'
        }
      </p>
    </div>
  );
};

export default RidePayment;
export { PAYMENT_METHODS };
